import { useMutation } from "@tanstack/react-query";
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { Badge } from "@lawline/ui/components/badge";
import { Button } from "@lawline/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@lawline/ui/components/dialog";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";
import { cn } from "@lawline/ui/core/cn";

import { formatDate } from "@platform/fmt";
import { setCommunicationStatusMutation } from "./communications.mutations";
import type { Communication, CommunicationStatus } from "./communications.types";

const STATUS_CLASS: Record<CommunicationStatus, string> = {
	open: "bg-primary/15 text-primary",
	closed: "bg-muted text-muted-foreground",
};

// Generic viewer for one communication kind (filtered upstream). Each row shows
// a one-line preview; clicking it opens a modal with the full message, where
// staff can close (or reopen) it.
export function CommunicationsTable({ rows }: { rows: Communication[] }) {
	const t = useTranslations("admin.communications.table");
	const tRoot = useTranslations("admin.communications");
	const [selected, setSelected] = useState<Communication | null>(null);
	const { mutate, isPending } = useMutation(setCommunicationStatusMutation);

	const columns = useMemo<ColumnDef<Communication>[]>(
		() => [
			{
				id: "from",
				header: t("columns.from"),
				cell: ({ row }) => (
					<div className="flex flex-col">
						<span className="font-medium">{row.original.userName || "-"}</span>
						{row.original.userEmail && (
							<span className="text-muted-foreground text-xs">{row.original.userEmail}</span>
						)}
					</div>
				),
			},
			{
				id: "tenant",
				header: t("columns.tenant"),
				cell: ({ row }) => (
					<div className="flex flex-col">
						<span>{row.original.tenantName || "-"}</span>
						{row.original.tenantSlug && (
							<span className="text-muted-foreground text-xs">{row.original.tenantSlug}</span>
						)}
					</div>
				),
			},
			{
				accessorKey: "body",
				header: t("columns.message"),
				cell: ({ row }) => (
					<span className="text-muted-foreground block max-w-xs truncate">{row.original.body}</span>
				),
			},
			{
				id: "status",
				header: t("columns.status"),
				cell: ({ row }) => (
					<Badge className={cn("border-transparent", STATUS_CLASS[row.original.status])}>
						{tRoot(`statuses.${row.original.status}`)}
					</Badge>
				),
			},
			{
				accessorKey: "createdAt",
				header: t("columns.created"),
				cell: ({ row }) => (
					<span className="whitespace-nowrap">{formatDate(row.original.createdAt)}</span>
				),
			},
		],
		[t, tRoot],
	);

	function toggleStatus(c: Communication) {
		mutate({ id: c.id, status: c.status === "open" ? "closed" : "open" });
		setSelected(null);
	}

	const detail = selected
		? [
				{
					label: tRoot("detail.email"),
					value: selected.userEmail,
					href: selected.userEmail ? `mailto:${selected.userEmail}` : undefined,
				},
				{ label: tRoot("detail.tenant"), value: selected.tenantName },
				{ label: tRoot("detail.slug"), value: selected.tenantSlug },
				{ label: tRoot("detail.date"), value: formatDate(selected.createdAt) },
			].filter((r) => r.value)
		: [];

	const table = useReactTable({
		data: rows,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<div className="overflow-hidden rounded-md border">
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map((group) => (
						<TableRow key={group.id}>
							{group.headers.map((header) => (
								<TableHead key={header.id}>
									{flexRender(header.column.columnDef.header, header.getContext())}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={columns.length}
								className="text-muted-foreground h-20 text-center text-sm"
							>
								{t("empty")}
							</TableCell>
						</TableRow>
					) : (
						table.getRowModel().rows.map((row) => (
							<TableRow
								key={row.id}
								onClick={() => setSelected(row.original)}
								className="cursor-pointer"
							>
								{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))
					)}
				</TableBody>
			</Table>

			<Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>{selected?.userName || "-"}</DialogTitle>
						<DialogDescription asChild>
							<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
								{detail.map((r) => (
									<Fragment key={r.label}>
										<dt className="text-muted-foreground">{r.label}</dt>
										<dd className="break-all">
											{r.href ? (
												<a href={r.href} className="text-primary hover:underline">
													{r.value}
												</a>
											) : (
												r.value
											)}
										</dd>
									</Fragment>
								))}
							</dl>
						</DialogDescription>
					</DialogHeader>
					<p className="bg-muted/30 max-h-[60vh] overflow-y-auto overscroll-y-contain rounded-md border p-3 text-sm whitespace-pre-wrap">
						{selected?.body}
					</p>
					<DialogFooter>
						{selected && (
							<Button
								variant={selected.status === "open" ? "default" : "outline"}
								disabled={isPending}
								onClick={() => toggleStatus(selected)}
							>
								{tRoot(selected.status === "open" ? "actions.close" : "actions.reopen")}
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
